import { show as openIntercom } from '@intercom/messenger-js-sdk';
import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import fullyRampedLogo from '../../assets/full-logo.svg';
import { AppRoutes, NAVBAR_HEIGHT } from '../../constants';
import { useAppSelector, useFeatureFlag, useWindowResize } from '../../hooks';
import { LD_FeatureFlags, OrganizationStatus, Permissions, SettingsTab } from '../../types';
import { conditionalArray } from '../../utils';
import { UploadModal } from '../modals';
import { ButtonColor, Icon, IconButton, TextButton } from '../shared';

const COMPACT_NAV_BAR_WIDTH = 800;

const NavBar = () => {
  // Hooks
  const location = useLocation();
  const navigate = useNavigate();

  const knowledgeHubFF = useFeatureFlag(LD_FeatureFlags.RELEASE_KNOWLEDGE_HUB);

  // Determines whether to display the navigation buttons as compact icon buttons.
  const windowWidth = useWindowResize();
  const isCompact = windowWidth <= COMPACT_NAV_BAR_WIDTH;

  // Redux
  const { organization, user } = useAppSelector((state) => state.auth);
  const { isUploadModalEnabled = false, isHiringModuleEnabled = false, status } = organization || {};
  const isTrialOrg = status === OrganizationStatus.TRIAL;

  // Check if the curr user is allowed to invite other users.
  const permissions = user?.permissions || [];
  const canManageKnowledgeHub = permissions.includes(Permissions.MANAGE_KNOWLEDGE_HUB);
  const canViewHiring = permissions.includes(Permissions.VIEW_CANDIDATE);

  // State
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  // Navigation tabs
  const tabs = [
    {
      id: AppRoutes.SIMULATIONS,
      icon: Icon.PHONE,
      title: 'Simulations',
    },
    {
      id: AppRoutes.REVIEW,
      icon: Icon.HISTORY,
      title: 'Review',
    },
    {
      id: AppRoutes.ANALYTICS,
      icon: Icon.BAR_CHART,
      title: 'Analytics',
    },
  ];

  const actions = [
    ...conditionalArray(canManageKnowledgeHub && knowledgeHubFF, {
      icon: Icon.KNOWLEDGE_HUB,
      tooltip: 'Go to Knowledge hub',
      onClick: () => navigate(AppRoutes.KNOWLEDGE_HUB),
    }),
    ...conditionalArray(canViewHiring && isHiringModuleEnabled, {
      disabled: isTrialOrg,
      icon: isTrialOrg ? Icon.LOCK : Icon.USER_SEARCH,
      tooltip: 'Go to Hiring',
      onClick: () => navigate(AppRoutes.SETTINGS + '/' + SettingsTab.HIRING),
    }),
    ...conditionalArray(isUploadModalEnabled, {
      icon: Icon.UPLOAD,
      tooltip: 'Upload file',
      onClick: () => setIsUploadModalOpen(true),
    }),
    {
      disabled: !window.Intercom,
      icon: Icon.CIRCLE_HELP,
      tooltip: window.Intercom ? 'Chat with support' : undefined,
      onClick: openIntercom,
    },
  ];

  const activeTab = useMemo(() => tabs.find((tab) => location.pathname.includes(tab.id))?.id, [location.pathname]);

  return (
    <>
      <div
        className="z-10 flex  w-full items-center justify-between bg-blue-900 px-4 py-2 shadow-[2px_2px_4px_0px_rgba(6,62,95,0.10)]"
        style={{ height: NAVBAR_HEIGHT }}
      >
        <img
          src={fullyRampedLogo}
          alt="FullyRamped Logo"
          onClick={() => navigate(AppRoutes.SIMULATIONS)}
          className="cursor-pointer"
        />
        <div className="flex items-center gap-2">
          {tabs.map((tab) => {
            const sharedProps = {
              active: activeTab === tab.id,
              color: ButtonColor.NAV_MENU,
              onClick: () => navigate(tab.id),
            };
            return isCompact ? (
              <IconButton key={tab.id} icon={tab.icon} tooltip={`Go to ${tab.title}`} {...sharedProps} />
            ) : (
              <TextButton key={tab.id} startIcon={tab.icon} text={tab.title} {...sharedProps} />
            );
          })}
        </div>
        <div className="flex items-center gap-3">
          {actions.map((action) => (
            <IconButton key={action.icon} color={ButtonColor.NAV_MENU} {...action} />
          ))}
        </div>
      </div>
      {/* Navbar Modals */}
      {isUploadModalOpen && <UploadModal onClose={() => setIsUploadModalOpen(false)} />}
    </>
  );
};

export default NavBar;
